import './DoorPage.css';
import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { SiteFooter } from '../../components/SiteFooter';
import { tDoor } from '../../copy/door';
import { LoginForm, type LoginFormProps } from './LoginForm';

/** O canal do YouTube e o Instagram do produto (os mesmos do rodapé, ORD-F-145). */
export const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@mapa.voto.26';
export const INSTAGRAM_URL = 'https://www.instagram.com/mapa.voto';

export interface DoorPageProps extends Pick<LoginFormProps, 'signIn' | 'requestReset'> {
  /** A marca do cabeçalho (o `Brand`, passado pela casca). */
  brand: ComponentChildren;
  /**
   * `live`: a capa pública a partir das 17h00 (os dois botões grandes). `login`: aberta pelo "Ver ao vivo" antes das
   * 17h00, com o formulário já aberto e o "Voltar".
   */
  variant: 'live' | 'login';
  /** Pacote só da capa (`capa_only`): sem "Entrar" (só o botão do YouTube e os links). */
  capaOnly: boolean;
  /** A live do YouTube (VITE_LIVE_URL); vazia = o canal. */
  liveUrl: string;
  /** Pesquisas, replay e vídeos; vazio = o link não aparece. */
  pollsUrl: string;
  replayUrl: string;
  videosUrl: string;
  /** "Voltar" (só na variante `login`): a página de antes. */
  onBack?: () => void;
  /** 26/10, abertura pós-apuração: a transmissão no passado (ficou gravada); o resto igual. */
  after?: boolean;
}

const external = { target: '_blank', rel: 'noopener noreferrer' } as const;

/**
 * A porta da apuração (ORD-F-153 e complemento de 20/10 20:40; DECISOES §84). Para quem não entrou: o aviso de que a
 * apuração é transmitida ao vivo no YouTube e no Instagram, o botão da live, o "Entrar e assistir no aplicativo" (que
 * abre o formulário ali mesmo, sem trocar de página), os links do site e o rodapé. Não lê o feed. Nenhum texto diz que os
 * números são restritos: a porta é do site.
 */
export function DoorPage(props: DoorPageProps) {
  const { brand, variant, capaOnly, pollsUrl, replayUrl, videosUrl, onBack, after = false } = props;
  const liveUrl = props.liveUrl || YOUTUBE_CHANNEL_URL;
  const login = variant === 'login';
  const [formOpen, setFormOpen] = useState(login);
  const [clicked, setClicked] = useState(false);
  const links = [
    pollsUrl ? { id: 'polls', href: pollsUrl, text: tDoor('door.links.polls') } : null,
    replayUrl ? { id: 'replay', href: replayUrl, text: tDoor('door.links.replay') } : null,
    videosUrl ? { id: 'videos', href: videosUrl, text: tDoor('door.links.videos') } : null,
  ].filter((l) => l !== null);

  return (
    <div class="door" data-testid="door-page" data-variant={variant}>
      <main id="conteudo" class="door__main">
        <header class="door__header">
          {brand}
          {login && onBack ? (
            <button type="button" class="door__back" data-testid="door-back" onClick={onBack}>
              {tDoor('door.back')}
            </button>
          ) : null}
        </header>

        {login && !capaOnly ? (
          <div class="door__form">
            <LoginForm signIn={props.signIn} requestReset={props.requestReset} autoFocus />
          </div>
        ) : null}

        <section class="door__hero" aria-labelledby="door-title">
          <p class="door__kicker">{tDoor('door.kicker')}</p>
          <h2 id="door-title" class={login ? 'door__title door__title--small' : 'door__title'}>
            {tDoor(after ? 'door.after.title' : 'door.title')}
          </h2>
          <p class="door__lede">{tDoor(after ? 'door.after.lede' : 'door.lede')}</p>
          <ul class="door__social" aria-label={tDoor('door.social.aria')}>
            <li>
              <a
                class="door__social-link"
                href={YOUTUBE_CHANNEL_URL}
                {...external}
                aria-label={tDoor('door.youtube.aria')}
                data-testid="door-youtube"
              >
                {tDoor('door.youtube')}
              </a>
            </li>
            <li>
              <a
                class="door__social-link"
                href={INSTAGRAM_URL}
                {...external}
                aria-label={tDoor('door.instagram.aria')}
                data-testid="door-instagram"
              >
                {tDoor('door.instagram')}
              </a>
            </li>
          </ul>
          {login ? null : (
            <div class="door__actions">
              <a
                class="door__btn door__btn--primary"
                href={liveUrl}
                {...external}
                aria-label={tDoor(after ? 'door.after.watch.aria' : 'door.watch.aria')}
                data-testid="door-watch"
              >
                {tDoor(after ? 'door.after.watch' : 'door.watch')}
              </a>
              {capaOnly ? null : (
                <button
                  type="button"
                  class="door__btn"
                  aria-expanded={formOpen ? 'true' : 'false'}
                  aria-controls="door-login"
                  data-testid="door-enter"
                  onClick={() => {
                    setClicked(true);
                    setFormOpen((o) => !o);
                  }}
                >
                  {tDoor('door.enter')}
                </button>
              )}
            </div>
          )}
          {!login && !capaOnly ? (
            <div class="door__form" id="door-login" hidden={!formOpen}>
              {formOpen ? (
                <LoginForm signIn={props.signIn} requestReset={props.requestReset} autoFocus={clicked} />
              ) : null}
            </div>
          ) : null}
        </section>

        {links.length > 0 ? (
          <nav class="door__links" aria-label={tDoor('door.links.aria')} data-testid="door-links">
            <ul class="door__link-list">
              {links.map((l) => (
                <li key={l.id}>
                  <a class="door__link" href={l.href} data-testid={`door-link-${l.id}`}>
                    {l.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
      </main>
      <div class="door__foot">
        <SiteFooter />
      </div>
    </div>
  );
}
